import * as React from 'react';
import { css, SerializedStyles } from '@emotion/react';
import { FloatingFocusManager, FloatingList } from '@floating-ui/react';
import { useForkRef } from '../../hooks';
import { FixMeLater } from '../../utils';
import { Paper } from '../Paper';
import { SelectMenuContext } from './SelectMenu.context';
import { useSelectMenu } from './useSelectMenu';
import { SelectMenuOption, SelectMenuOptionProps } from './SelectMenuOption/SelectMenuOption';
import { SelectMenuButton } from './SelectMenuButton/SelectMenuButton';
import { SvgIconProps } from '../SvgIcon';
import { SelectMenuOptionData } from './SelectMenu.types';
import { POPOVER_STATIC_STYLES, usePopoverAnimationCssVariables } from '../Popover/Popover.styles';

type StyleClasses = 'inner';

// Static styles
const STATIC_STYLES: { [key in StyleClasses]: SerializedStyles } = {
    inner: css`
        border-radius: 8px;
    `,
};

export type SelectMenuProps = {
    /**
     * The child `SelectMenuOption` components
     */
    children: React.ReactElement<SelectMenuOptionProps> | React.ReactElement<SelectMenuOptionProps>[];

    /**
     * The value of the currently selected option. Required if the component is controlled.
     */
    value?: string;

    /**
     * The default value. Use when the component is not controlled.
     */
    defaultValue?: string;

    /**
     * Callback fired when the value changes.
     * @param {SelectMenuValue} value The value of the selected option.
     */
    onChange?: (value: string) => void;

    /**
     * An svg icon component. This will be displayed on the menu button.
     */
    icon?: React.ReactElement<SvgIconProps>;
};

export const SelectMenu: React.ForwardRefExoticComponent<SelectMenuProps & React.RefAttributes<HTMLElement>> =
    React.forwardRef(function SelectMenu(
        props: SelectMenuProps,
        ref: React.ForwardedRef<FixMeLater>
    ): React.JSX.Element {
        const { children, value, defaultValue, onChange, icon, ...other } = props;

        // Iterate the child `SelectMenuOption` elements and compose an array of option data objects.
        // These are required by the hook to manage the selected value.
        const options: Array<SelectMenuOptionData> = React.useMemo(() => {
            const elements = React.Children.toArray(children).filter(
                (child) => React.isValidElement(child) && child.type === SelectMenuOption
            ) as React.ReactElement<SelectMenuOptionProps>[];
            return elements.map((element, index) => {
                return { index, value: element.props.value, label: element.props.children };
            });
        }, [children]);

        const selectMenuContext = useSelectMenu({ value, defaultValue, onChange, options });

        const { context, getFloatingProps, refs, listRef, transitionStatus, isMounted, placement } = selectMenuContext;

        const { floatingStyles } = context;

        const handleButtonRef = useForkRef(ref, refs.setReference);

        // Prepare CSS variables
        const cssVariables = usePopoverAnimationCssVariables();

        return (
            <SelectMenuContext.Provider value={selectMenuContext}>
                <SelectMenuButton ref={handleButtonRef} icon={icon} {...other} />
                {isMounted && (
                    <FloatingFocusManager context={context} modal={false}>
                        {/* Floating element */}
                        <div
                            ref={refs.setFloating}
                            style={floatingStyles}
                            css={POPOVER_STATIC_STYLES.floating}
                            {...getFloatingProps()}
                        >
                            {/* Inner element (this animates) */}
                            <Paper
                                style={cssVariables}
                                css={[POPOVER_STATIC_STYLES.inner, STATIC_STYLES.inner]}
                                shadow="m"
                                border={1}
                                py={2}
                                overflowY="auto"
                                data-status={transitionStatus}
                                data-placement={placement}
                            >
                                <FloatingList elementsRef={listRef}>{children}</FloatingList>
                            </Paper>
                        </div>
                    </FloatingFocusManager>
                )}
            </SelectMenuContext.Provider>
        );
    });
